import { useContext, useEffect, useState } from 'react';
import Link from 'next/link';
import { Skeleton } from '@components';
import { LegalEntityContext } from '@context';
import { Error } from '@public';
import { ApiStatus, ClientType } from '@types';
import { isNull } from 'lodash';

import styles from './clientItem.module.scss';

export type ClientItemProps = {
	clientItem?: ClientType;
	selectable?: boolean;
	showAddress?: boolean;
	testId?: string;
	onSelect?: (client: ClientType) => void;
};

export const ClientItem = ({
	clientItem,
	selectable = false,
	showAddress = true,
	testId,
	onSelect,
}: ClientItemProps) => {
	const { setLegalEntity, legalEntityRequestStatus: loadingStatus } =
		useContext(LegalEntityContext);
	const [client, setClient] = useState(clientItem);

	useEffect(() => {
		setClient(clientItem);
	}, [clientItem]);

	const emptyContent = () => {
		return (
			<>
				<div
					className={` ${styles.entityContainer} ${
						styles[`entityContainer${selectable === true && '-selectable'}`]
					} `}
				>
					<div>
						<h5 data-testid="empty-name">No entities selected</h5>
					</div>
					<div className={styles.entityInfoRow}>
						<div className={styles.entityInfo}>
							<p className={styles.detail}>
								There are no legal entities associated with your account.
							</p>
						</div>
					</div>
					<div className={styles.entityInfoRow}>
						<div className={styles.entityInfo}>
							<p className={styles.detail}>
								<Error customStyles={styles['detail-icon']} />
								&nbsp;Entity missing?{' '}
								<Link href={'/contact'} passHref className={styles.help}>
									Contact us
								</Link>
							</p>
						</div>
					</div>
				</div>
			</>
		);
	};
	return !(loadingStatus == ApiStatus.ERROR || loadingStatus == ApiStatus.EMPTY) ? (
		<div
			className={` ${styles.entityContainer} ${styles[`entityContainer${selectable === true && '-selectable'}`]} `}
			onClick={() => {
				if (selectable && clientItem) {
					setLegalEntity(clientItem);
					onSelect?.(clientItem);
				}
			}}
			data-testid={testId}
		>
			{loadingStatus != ApiStatus.FULFILLED ? (
				<Skeleton
					customStyles={[styles['skeleton-clientRow'], styles['w90'], styles['h20']].join(' ')}
				/>
			) : (
				<div className={styles['name-container']}>
					<div className={styles.tooltip}>
						<h5 className={styles.name} data-testid="name">
							{clientItem ? clientItem.name : ''}
						</h5>
						<div className={styles.tooltiptext}>{clientItem ? clientItem.name : ''}</div>
					</div>
				</div>
			)}
			<div className={styles.entityInfoRow}>
				<div className={styles.entityInfo}>
					{loadingStatus == ApiStatus.FULFILLED ? (
						<p className={styles.reference} data-testid="reference">
							{client?.reference}
						</p>
					) : (
						<div className={styles.detail}>
							<Skeleton
								customStyles={[styles['skeleton-clientRow'], styles['w50'], styles['h10']].join(
									' ',
								)}
							/>
						</div>
					)}
				</div>
			</div>
			{showAddress && (
				<div className={styles.entityInfoRow}>
					<div className={styles.entityInfo}>
						{loadingStatus == ApiStatus.FULFILLED ? (
							<p className={styles.detail} data-testid="detail">
								{client ? (
									`${client.postalAddress.addressLine1} ${
										!isNull(client.postalAddress.addressLine2)
											? client.postalAddress.addressLine2
											: ''
									} ${client.postalAddress.postalCode} ${client.postalAddress.suburb}`
								) : (
									<>
										<p className={styles.skeleton}></p>
										<p className={styles.skeleton}></p>
									</>
								)}
							</p>
						) : (
							<div className={styles.detail}>
								<Skeleton customStyles={[styles['skeleton-clientRow'], styles['h15']].join(' ')} />
								<Skeleton
									customStyles={[styles['skeleton-clientRow'], styles['w75'], styles['h15']].join(
										' ',
									)}
								/>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	) : (
		emptyContent()
	);
};
